import dayjs from 'dayjs';
import { useQueries } from 'react-query';

import banner from 'assets/images/home/bg-banner.png';
import bgBackground from 'assets/images/home/bg-not-found.png';
import { getNewsBlogService } from 'services/news';
import { BlogNewsData, MappedBlogData, NewDetail } from 'services/news/type';

export type NewsCardItem = {
  img: string;
  title: string;
  dateTime: string;
  slug?: string;
  description?: string;
  isPopular?: boolean;
  categoryNames?: string[];
  displayOrder: number;
  createAt?: string;
  updateAt?: string;
};

// Main mapper — handles your exact API response
export const mapNewsResponseToCards = (
  response: BlogNewsData[]
): NewsCardItem[] => response.map((item) => {
  const news = item.newsData;
  const thumbnailPath = news?.thumbnail?.path || '';

  const imgUrl = thumbnailPath;

  return {
    img: imgUrl || bgBackground, // fallback image
    title: news?.title || 'Untitled',
    slug: news?.slug,
    description: news?.description,
    isPopular: news?.isPopular,
    dateTime: news?.publishedAt
      ? (news.publishedAt)
      : dayjs().toISOString(), // fallback to now
    displayOrder: news?.displayOrder || 0,
    // Optional: include category names
    categoryNames: item.categories.map((cat) => cat.name),
    createAt: news?.createdAt,
    updateAt: news?.updatedAt
  };
});

export type VideoItem = {
  link?: string;
  name?: string;
  thumbnail?: string; // optional: for preview
  title?: string; // optional: if you have title
};

// Map video block → clean array of videos
export const mapVideosResponse = (
  videosBlock: any
): VideoItem[] => {
  if (!videosBlock || !Array.isArray(videosBlock)) {
    return [];
  }

  return videosBlock
    .map((item: any) => {
      if (!item) return null;

      return {
        link: item.link.data,
        name: item.name.data,
      };
    })
    .filter(Boolean) as VideoItem[]; // remove nulls
};

// Helper to extract ID
export const extractYouTubeId = (url: string): string | null => {
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/i;
  const match = url.match(regex);
  return match ? match[1] : null;
};

export const getYouTubeThumbnail = (url: string | null | undefined): string => {
  if (!url) return bgBackground;

  const videoId = extractYouTubeId(url);
  if (!videoId) return bgBackground;

  // Try best quality first → fallback if image fails to load
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

export const getFeaturedVideoAndOthers = (
  videosBlock: any
): {
  featured: (VideoItem & { thumbnail: string }) | null;
  others: (VideoItem & { thumbnail: string })[];
} => {
  // 1. Map raw videos
  const videos = mapVideosResponse(videosBlock);
  if (videos.length === 0) {
    return { featured: null, others: [] };
  }

  // 2. Add thumbnails
  const videosWithThumbnail = videos.map((video) => ({
    ...video,
    thumbnail: video.link
      ? getYouTubeThumbnail(video.link)
      : bgBackground,
  }));

  // 3. Split: first = featured, rest = slider
  return {
    featured: videosWithThumbnail[0] ?? null,
    others: videosWithThumbnail.slice(1),
  };
};

type BlogSectionWithData = {
  title: string;
  endpoint: string;
  queries: {
    page: number;
    limit: number;
    sortBy: string;
    sortType: string;
    categoryIds: number[];
  };
};

// Extract clean sections from CMS
const extractBlogSections = (data: any[] = []): BlogSectionWithData[] => (data || [])
  .filter((item: any): item is {
    posts: any; title: { data: string }
  } => !!item?.posts?.data?.endpoint && !!item?.posts?.data?.queries)
  .map((item) => ({
    title: item.title?.data || 'Untitled',
    endpoint: item.posts.data.endpoint,
    queries: item.posts.data.queries,
  }));

export const useBlogSections = (blogPageData: any) => {
  const sections = extractBlogSections(blogPageData?.blocks?.blogs?.blogs?.data);

  const queries = useQueries(
    sections.map((section: { title: any; queries: any; endpoint: string }) => ({
      queryKey: ['blogs', section.title, section.queries],
      queryFn: async () => {
        const resource = section.endpoint
          .split('/')
          .filter(Boolean)
          .pop() || 'blogs';

        const result = await getNewsBlogService({
          endPoint: resource,
          params: section.queries,
        });

        return {
          title: section.title,
          items: result, // your blog posts
        };
      },
      enabled: !!blogPageData && section.queries.categoryIds.length > 0,
      refetchOnWindowFocus: false,
    }))
  );

  // Return clean results
  return {
    data: queries.map((q) => q.data).filter(Boolean),
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
};

// eslint-disable-next-line import/prefer-default-export
export const mapBlogData = (raw?: NewDetail, locale = 'vi'): MappedBlogData => {
  // Default fallback
  if (!raw?.translations?.[locale]) {
    return {
      bannerId: 0,
      storyHook: null,
      blocks: [],
      compare: null,
      conclusion: null,
      related: [],
      botKey: '',
    };
  }

  const t = raw.translations[locale];

  const bannerImg = t.thumbnail?.path || banner;

  return {
    bannerId: t.bannerId || 0,
    botKey: t.botKey || '',

    // Story Hook
    storyHook: t.storyHook && t.storyHook.title
      ? {
        title: t.storyHook.title || '',
        description: t.storyHook.description || '',
        imgSrc: t.storyHook.thumbnail?.path
      }
      : null,

    // Dynamic Blocks (from items[])
    blocks: Array.isArray(t.items)
      ? t.items.map((item) => ({
        title: item.title || '',
        description: item.description || '',
        imgSrc: (item.thumbnail.path) || bannerImg,
      }))
      : [],

    compare: {
      title: t.summary?.title || '',
      items: t.summary.rows.map((item) => ({
        title: item.title || '',
        description: item.description || '',
        imgSrc: item.thumbnail.path || bannerImg,
        imgAlt: item.thumbnail.alt || '',
        imgTitle: item.thumbnail.title || '',
      }) as any),
    },

    // Conclusion
    conclusion: t.conclusion && (t.conclusion.title || t.conclusion.description)
      ? {
        title: t.conclusion.title || '',
        description: t.conclusion.description || '',
        imgSrc: t.conclusion.thumbnail.path || bannerImg,
      }
      : null,

    // Related Posts (from relatedByCategories)
    related: Array.isArray(raw.relatedByBlog)
      ? raw.relatedByBlog.map((item) => ({
        slug: item.slug || item.slug || '',
        title: item.title || 'Untitled',
        description: item.description || '',
        imgSrc: (item.thumbnail?.path) || bannerImg,
        imgAlt: item.thumbnail?.alt || '',
      }))
      : [],
  };
};
